import React from 'react';
import { type Icon, Globe, Play, X } from '../../design-system/icons';
import { InstagramIcon, TiktokIcon, YoutubeIcon } from '../../components/icons';
import { youTubeThumbnailUrl } from '../../lib/youtubeThumbnail';
import { useVideoTitle } from '../../lib/useVideoTitle';
import { Card } from '../../components/ui/card';
import { resolvePlatform } from '@discovr/types';
import type { Platform } from '@discovr/types';

// Shared presentational building blocks for the Campaign Details tab, used by
// both the read view and the editable form so the two stay visually identical.

// Title-case for enum-ish strings (platform, goal, status, payout_model).
export function titleCase(raw: unknown): string {
  const s = String(raw ?? '')
    .trim()
    .replace(/[_-]+/g, ' ');
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

// Acronyms upper-cased (USA / UAE / UK), everything else title-cased, since
// plain titleCase would wrongly give "Usa"/"Uae".
const REGION_LABELS: Record<string, string> = {
  usa: 'USA',
  uae: 'UAE',
  uk: 'UK',
  india: 'India',
  global: 'Global',
};
export function formatRegion(raw: string): string {
  return REGION_LABELS[raw.trim().toLowerCase()] ?? titleCase(raw);
}

const SAFE_REFERENCE_URL_SCHEMES = new Set(['http:', 'https:']);

/** Allows only http(s) schemes for a clickable reference link — reference URLs
 *  are free-text user input rendered into other viewers' browsers as an href,
 *  so javascript:/data:/etc. must be rejected. */
export function isSafeReferenceUrl(url: string): boolean {
  try {
    return SAFE_REFERENCE_URL_SCHEMES.has(new URL(url).protocol);
  } catch {
    return false;
  }
}

// Phosphor's own icon component type — matches the design-system icon exports
// exactly (weight is IconWeight, not a loose string).
export type IconType = Icon;

/** Card with an icon-tile header + a bottom-bordered body (Figma card chrome). */
export function CardShell({ icon: Icon, title, children }: { icon: IconType; title: string; children: React.ReactNode }) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      {/* py-5 + the size-9 icon = the 76px header height Figma specs. */}
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
          <Icon size={20} />
        </span>
        <h3 className="text-heading-h7 font-medium text-foreground">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </Card>
  );
}

/** Icon + heading sub-section; `divider` adds the top separator + spacing. */
export function Section({
  icon: Icon,
  title,
  divider,
  children,
}: {
  icon: IconType;
  title: string;
  divider?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={divider ? 'border-t border-border pt-6' : undefined}>
      <div className="mb-4 flex items-center gap-2 text-foreground">
        <Icon size={18} />
        <h4 className="text-body-md-medium">{title}</h4>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-5">{children}</div>
    </div>
  );
}

/** Grey label above a value. `id` + `badge` are optional hooks for the admin
 *  diff overlay — both no-ops for the plain read view. */
export function Field({
  label,
  children,
  id,
  badge,
}: {
  label: string;
  children: React.ReactNode;
  id?: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="min-w-0 scroll-mt-24" id={id}>
      <div className="mb-1.5 flex items-center gap-1.5 text-body-sm-regular text-muted-foreground">
        <span>{label}</span>
        {badge}
      </div>
      <div className="text-body-md-medium text-foreground">{children}</div>
    </div>
  );
}

/** Light rounded-full value pill. `variant` renders the diff overlay's removed
 *  (struck-through) and added (dashed border) states, both with no fill so the
 *  change reads via the border/strike rather than a coloured chip. */
export function Pill({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'removed' | 'added';
}) {
  if (variant === 'removed') {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-body-sm-medium text-muted-foreground line-through">
        {children}
      </span>
    );
  }
  if (variant === 'added') {
    return (
      <span className="inline-flex items-center rounded-full border border-dashed border-border px-3 py-1 text-body-sm-medium text-muted-foreground">
        {children}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-body-sm-medium text-foreground">
      {children}
    </span>
  );
}

/** Label + a wrap of pills; renders nothing when there are no values (and no
 *  badge/id override forcing it to render for the diff overlay). */
export function PillField({
  label,
  values,
  id,
  badge,
  override,
}: {
  label: string;
  values: string[];
  id?: string;
  badge?: React.ReactNode;
  /** Replaces the values-derived pills entirely (the admin diff overlay's
   *  kept/removed/added rendering) — also bypasses the empty-values no-render. */
  override?: React.ReactNode;
}) {
  if (!values.length && !badge && !override) return null;
  return (
    <div className="min-w-0 scroll-mt-24" id={id}>
      <div className="mb-1.5 flex items-center gap-1.5 text-body-sm-regular text-muted-foreground">
        <span>{label}</span>
        {badge}
      </div>
      {override ?? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v, i) => (
            <Pill key={`${v}-${i}`}>{v}</Pill>
          ))}
        </div>
      )}
    </div>
  );
}

/** Small brand mark for a platform value. Accepts a canonical Platform or a
 *  loose backend string (resolved via resolvePlatform; TikTok handled
 *  explicitly). An unresolved platform falls back to the neutral Globe glyph. */
export function PlatformMark({
  platform,
  size = 20,
}: {
  platform: Platform | string | null;
  size?: number;
}) {
  const raw = typeof platform === 'string' ? platform.trim().toLowerCase() : platform;
  if (raw === 'tiktok') return <TiktokIcon size={size} />;
  const resolved = typeof raw === 'string' ? resolvePlatform(raw) : raw;
  switch (resolved) {
    case 'youtube':
      return <YoutubeIcon size={size} />;
    case 'instagram':
      return <InstagramIcon size={size} />;
    default:
      return <Globe size={size} aria-label="Unknown platform" />;
  }
}

/** Full-width chip used for a single Do / Don't line. */
export function BriefChip({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg bg-muted px-3 py-2.5 text-body-sm-regular text-foreground">{children}</div>;
}

/** Reference video card — thumbnail + play + platform mark, with the real video
 *  title (via oEmbed) when resolvable. Pass `onRemove` in an editable context
 *  to render a remove overlay (the read view omits it). */
export function ReferenceCard({ url, onRemove }: { url: string; onRemove?: () => void }) {
  const platform = resolvePlatform(url);
  const thumb = youTubeThumbnailUrl(url);
  const title = useVideoTitle(url, platform);
  // Defense in depth: only http(s) links become a clickable href, in case an
  // unsafe scheme slipped in from an older campaign or a pre-check write path.
  const safeHref = isSafeReferenceUrl(url) ? url : undefined;
  return (
    <div className="relative w-[220px] max-w-full overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-sm">
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove reference"
          className="absolute top-2 right-2 z-10 flex size-6 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-black"
        >
          <X size={12} weight="bold" />
        </button>
      )}
      <a
        href={safeHref}
        target={safeHref ? '_blank' : undefined}
        rel={safeHref ? 'noreferrer' : undefined}
        aria-disabled={!safeHref}
        className={`block ${safeHref ? '' : 'pointer-events-none opacity-60'}`}
      >
        <div className="relative aspect-video w-full overflow-hidden bg-stone-900">
          {thumb ? <img src={thumb} alt="" className="size-full object-cover" /> : null}
          <span className="absolute top-2 left-2 flex size-6 items-center justify-center rounded-md bg-white/90 shadow-sm">
            <PlatformMark platform={platform} size={14} />
          </span>
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-9 items-center justify-center rounded-full bg-white/90 text-stone-900 shadow-sm">
              <Play size={16} weight="fill" />
            </span>
          </span>
        </div>
        <div className="px-3 py-2.5">
          <div className="truncate text-body-sm-medium text-foreground">
            {safeHref ? (title ?? 'Reference') : 'Invalid link'}
          </div>
          <div className="mt-0.5 text-caption-regular text-muted-foreground capitalize">{platform}</div>
        </div>
      </a>
    </div>
  );
}

/** Author initial in a gradient sphere — the comment/edit-request author chip,
 *  shared so its gradient + sizing stay identical everywhere it appears. */
export function AuthorAvatar({ name, size = 'md' }: { name: string | null | undefined; size?: 'sm' | 'md' }) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-linear-to-br from-fuchsia-500 to-purple-600 font-medium text-white ${
        size === 'sm' ? 'size-5 text-[9px]' : 'size-6 text-[10px]'
      }`}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </span>
  );
}
